import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "./Seo";
import { Check, Info, Users, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../api/client";

const PLAN_FEATURES = {
  basic: {
    tagline: "For solo mechanics, salons & dentists getting started.",
    perks: [
      "Automated WhatsApp service reminders",
      "AI-suggested service intervals",
      "Payment collection + invoice reminders",
      "Daily 9:00 AM digest",
      "Email + WhatsApp support",
    ],
  },
  standard: {
    tagline: "For small teams scaling their reminder workflow.",
    perks: [
      "Everything in Basic",
      "CSV bulk import for customers & invoices",
      "KM-based reminders (car & bike)",
      "Priority support",
    ],
    popular: true,
  },
  pro: {
    tagline: "For busy distributors & multi-location businesses.",
    perks: [
      "Everything in Standard",
      "Distributor payment-collection module",
      "Delivery status analytics",
      "VIP support",
    ],
  },
};

const PLAN_ORDER = ["basic", "standard", "pro"];

export default function Pricing() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null); // auth state
  const [checkoutBusy, setCheckoutBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data?.user || r.data)).catch(() => setMe(null));
  }, []);

  const handleSubscribe = async (planKey) => {
    if (!me) {
      navigate("/login?next=/pricing");
      return;
    }
    setCheckoutBusy(planKey);
    try {
      const { data } = await api.post("/billing/create-subscription", { plan: planKey });
      if (!window.Razorpay) {
        toast.error("Razorpay failed to load. Please refresh and try again.");
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "DueMate",
        description: `${planKey.charAt(0).toUpperCase() + planKey.slice(1)} plan · 14-day free trial`,
        image: "/icons/apple-touch-icon.png",
        prefill: {
          name: me?.name || "",
          email: me?.email || "",
          contact: me?.business_phone || "",
        },
        theme: { color: "#4361EE" },
        handler: function () {
          toast.success("Subscription set up! Your 14-day free trial has started.");
          navigate("/dashboard");
        },
        modal: {
          ondismiss: () => setCheckoutBusy(null),
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Please try again"}`);
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start checkout";
      toast.error(msg);
    } finally {
      setCheckoutBusy(null);
    }
  };

  return (
    <>
      <Seo
        title="Pricing — DueMate"
        description="Simple monthly pricing for DueMate. Start with a 14-day free trial. Basic ₹199, Standard ₹299, Pro ₹599."
      />

      <section className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-12 pb-6 text-center">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4361EE]">Pricing</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          Start with a 14-day free trial
        </h1>
        <p className="text-base text-[#4A4A4A] font-semibold mt-4 max-w-xl mx-auto">
          No charge for the first 14 days. Cancel anytime. All plans include automated WhatsApp reminders, invoices and AI-suggested intervals.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-4 sm:px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          {PLAN_ORDER.map((key) => {
            const plan = config?.plans?.[key];
            const meta = PLAN_FEATURES[key];
            const isCurrent = me?.plan === key;
            const popular = meta.popular;
            return (
              <div
                key={key}
                data-testid={`plan-card-${key}`}
                className={`nb-card p-6 flex flex-col ${
                  popular ? "bg-[#FFD166]" : "bg-white"
                } relative`}
              >
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border-2 border-black">
                    Most popular
                  </div>
                )}
                <h3 className="font-heading text-2xl font-black capitalize">{key}</h3>
                <p className="text-sm text-[#4A4A4A] font-semibold mt-1 min-h-[40px]">{meta.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-5xl font-black">
                    ₹{plan?.price_inr ?? "—"}
                  </span>
                  <span className="text-sm font-semibold text-[#4A4A4A]">/month</span>
                </div>
                <div className="flex items-center gap-4 mt-4 py-3 border-y-2 border-black/10">
                  <span className="flex items-center gap-1.5 text-sm font-bold">
                    <Users size={16} weight="bold" />
                    {plan?.customer_limit ?? "—"} customers
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-bold">
                    <ChatCircle size={16} weight="bold" />
                    {plan?.wa_msg_limit ?? "—"} msgs/mo
                  </span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {meta.perks.map((p) => (
                    <li key={p} className="flex gap-2 text-sm font-semibold">
                      <Check size={18} weight="bold" className="text-[#06D6A0] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${key}-btn`}
                  disabled={isCurrent || checkoutBusy === key}
                  onClick={() => handleSubscribe(key)}
                  className={`nb-btn mt-6 w-full py-3 font-black ${
                    isCurrent
                      ? "bg-gray-200 cursor-not-allowed"
                      : popular
                      ? "bg-black text-white"
                      : "bg-[#4361EE] text-white"
                  }`}
                >
                  {isCurrent ? "Your current plan" : checkoutBusy === key ? "Opening…" : "Start 14-day trial"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="nb-card p-5 mt-8 bg-white">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#FFD166] border-2 border-black rounded-md flex items-center justify-center flex-shrink-0">
              <Info size={18} weight="bold" />
            </div>
            <div className="text-sm text-[#4A4A4A] font-semibold space-y-2">
              <p>
                Prices are in INR and billed monthly via Razorpay. Your card is securely tokenised — we never store card details. You will NOT be charged during the 14-day free trial; the first ₹ charge happens on day 15.
              </p>
              <p>
                Cancel anytime from Settings. See our <Link to="/refund-and-cancellation" className="underline text-[#4361EE]">Refund &amp; Cancellation Policy</Link> and <Link to="/terms-and-conditions" className="underline text-[#4361EE]">Terms &amp; Conditions</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
